import React from "react";

import { ShieldCheckIcon } from "manager/components/app-summary/modals/verify-idfraudrisk-modal/ShieldCheckIcon";
import { StrokeCheckIcon } from "manager/components/app-summary/modals/verify-idfraudrisk-modal/StrokeCheckIcon";
import { useDownloadApprovalLetter } from "manager/hooks/api";
import { Modal } from "shared/components/Modal/Modal";
import { LockIcon } from "shared/components/vega/icons";

import StatusIcon, { StatusType, getStatusColor } from "../../StatusIcon";

import { CardDetailItem } from "./ApplicantGroupCard";
import { Vero1UpdatedNote } from "./Vero1UpdatedNote";

// Define types for the modal details
export interface ModalRecordItem {
  text: string;
  id?: string;
}

export interface ModalSubItem {
  label: string;
  value: string;
  status?: string;
  records?: ModalRecordItem[];
  id?: string;
}

export interface ModalDetailItem {
  label: string;
  value: string;
  status?: string;
  subItems?: ModalSubItem[];
  id?: string;
}

export interface ModalSection {
  title?: string;
  items: ModalDetailItem[];
  id?: string;
}

export interface ApplicantDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardTitle: string;
  cardDetails: CardDetailItem[];
  cardModalDetails?: ModalSection[];
  status: StatusType;
  vero1Edited?: boolean;
  onReviewChanges?: (e: React.MouseEvent) => void;
  cosignApproved?: boolean;
  cosignUuid?: string;
  applicantName?: string;
  resetIdFraudRisk?: {
    atResetLimit: boolean;
    onReset: () => void;
    disabled?: boolean;
    disabledReason?: string | null;
  };
  verifyIdFraudRisk?: {
    onVerify: () => void;
    disabled?: boolean;
    disabledReason?: string | null;
    terminalLabel?: string | null;
  };
  /** Shown after an eligible check was manually verified (PRODUCT-2164/2244). */
  manualIdFraudRiskVerification?: {
    reviewerDisplay?: string | null;
    verifiedAtDisplay?: string | null;
    originalTierLabel?: string | null;
  } | null;
}

// Styles for consistent item appearance
const itemStyle = {
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "16px",
  background: "#fafafa",
  textAlign: "left" as const,
};

const modalContentWidth = { width: "100%", maxWidth: "500px" };

const renderModalDetailItem = (item: ModalDetailItem) => {
  const hasSubItems = item.subItems && item.subItems.length > 0;

  return (
    <div
      key={item.id || `item-${item.label}-${item.value}`}
      className="vega margin-bottom-medium"
      style={itemStyle}
    >
      <div className="vega text" style={{ lineHeight: 1.5 }}>
        {item.label ? (
          <>
            <strong
              style={
                item.status ? { color: getStatusColor(item.status) } : undefined
              }
            >
              {item.label}:
            </strong>{" "}
            <span
              style={
                item.status ? { color: getStatusColor(item.status) } : undefined
              }
            >
              {item.value}
            </span>
          </>
        ) : (
          <span style={{ fontWeight: 500 }}>{item.value}</span>
        )}
      </div>

      {hasSubItems && (
        <ul
          style={{
            margin: "12px 0 0",
            paddingLeft: "20px",
            listStyleType: "disc",
          }}
        >
          {item.subItems.map((subItem) => (
            <li
              key={subItem.id || `subitem-${subItem.label}-${subItem.value}`}
              className="vega text"
              style={{ marginBottom: "8px", lineHeight: 1.45 }}
            >
              {subItem.label ? (
                <>
                  <strong
                    style={
                      subItem.status
                        ? { color: getStatusColor(subItem.status) }
                        : undefined
                    }
                  >
                    {subItem.label}:
                  </strong>{" "}
                  <span
                    style={
                      subItem.status
                        ? { color: getStatusColor(subItem.status) }
                        : undefined
                    }
                  >
                    {subItem.value}
                  </span>
                </>
              ) : (
                subItem.value
              )}
              {subItem.records && subItem.records.length > 0 && (
                <ul
                  style={{
                    margin: "8px 0 0",
                    paddingLeft: "20px",
                    listStyleType: "circle",
                  }}
                >
                  {subItem.records.map((record) => (
                    <li
                      key={record.id || `record-${record.text}`}
                      className="vega text text-small"
                      style={{ marginBottom: "4px" }}
                    >
                      {record.text}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const renderCardDetailItem = (detail: CardDetailItem) => (
  <div
    key={detail.id || `detail-${detail.label}-${detail.value}`}
    className="vega margin-bottom-medium"
    style={itemStyle}
  >
    <div className="vega text" style={{ lineHeight: 1.5 }}>
      {detail.label ? (
        <>
          <strong
            style={
              detail.status
                ? { color: getStatusColor(detail.status) }
                : undefined
            }
          >
            {detail.label}:
          </strong>{" "}
          <span
            style={
              detail.status
                ? { color: getStatusColor(detail.status) }
                : undefined
            }
          >
            {detail.value}
          </span>
        </>
      ) : (
        <span>{detail.value}</span>
      )}
    </div>
  </div>
);

/**
 * Modal component for displaying detailed applicant information
 */
const ApplicantDetailModal: React.FC<ApplicantDetailModalProps> = ({
  isOpen,
  onClose,
  cardTitle,
  cardDetails,
  cardModalDetails,
  status,
  vero1Edited = false,
  onReviewChanges,
  cosignApproved = false,
  cosignUuid,
  applicantName,
  resetIdFraudRisk,
  verifyIdFraudRisk,
  manualIdFraudRiskVerification,
}) => {
  const { downloadApprovalLetter } = useDownloadApprovalLetter();

  // Check if we should show Cosign static content
  const showCosignContent =
    cosignApproved && cardTitle === "Income" && cosignUuid;

  const modalTitle =
    !showCosignContent && applicantName
      ? `${cardTitle} \u00b7 ${applicantName}`
      : cardTitle;

  // Use modal details if available, otherwise fall back to card details
  const hasModalDetails = cardModalDetails && cardModalDetails.length > 0;

  const handleViewApprovalLetter = () => {
    if (cosignUuid) {
      downloadApprovalLetter({ cosignUuid });
    }
  };

  const FRAUD_RISK_SECTION_ID = "idfraudrisk-modal-section";
  const hasFraudRiskSection = Boolean(
    cardModalDetails?.some((section) => section.id === FRAUD_RISK_SECTION_ID)
  );

  // The reset affordance is only passed for a BLOCKED check (the backend reset
  // endpoint rejects non-blocked records), so this panel always renders the
  // blocked treatment.
  const resetBlock = resetIdFraudRisk ? (
    <div
      className="app-summary-v2 idfraudrisk-modal-blocked-box"
      data-testid="idfraudrisk-modal-blocked-box"
      style={modalContentWidth}
    >
      <div className="idfraudrisk-modal-blocked-heading">
        <span
          className="idfraudrisk-pill blocked"
          data-testid="idfraudrisk-modal-blocked-pill"
        >
          <LockIcon className="idfraudrisk-pill-icon" aria-hidden="true" />
          BLOCKED
        </span>
      </div>
      <p className="idfraudrisk-modal-blocked-copy">
        {resetIdFraudRisk.disabledReason ||
          "Applicant can't proceed -- reset to let them re-enter their SSN."}
      </p>
      <button
        type="button"
        data-testid="reset-idfraudrisk-modal-button"
        onClick={() => {
          if (!resetIdFraudRisk.atResetLimit && !resetIdFraudRisk.disabled) {
            onClose();
            resetIdFraudRisk.onReset();
          }
        }}
        disabled={resetIdFraudRisk.atResetLimit || resetIdFraudRisk.disabled}
        className="idfraudrisk-modal-reset-button"
      >
        Reset this check
      </button>
      {resetIdFraudRisk.atResetLimit && !resetIdFraudRisk.disabledReason && (
        <div className="idfraudrisk-modal-reset-limit">
          The maximum number of resets for this applicant has been reached.
        </div>
      )}
    </div>
  ) : null;

  const verifyBlock = verifyIdFraudRisk ? (
    <div
      className="app-summary-v2 idfraudrisk-modal-verify-box"
      data-testid="idfraudrisk-modal-verify-box"
      style={modalContentWidth}
    >
      <div className="idfraudrisk-modal-verify-text">
        <span
          className="idfraudrisk-pill warn"
          data-testid="idfraudrisk-modal-verify-pill"
        >
          {verifyIdFraudRisk.terminalLabel ?? "Needs Review"}
        </span>
        <span className="idfraudrisk-modal-verify-copy">
          {verifyIdFraudRisk.disabledReason ||
            "Record what you reviewed to verify identity and re-run Lumen."}
        </span>
      </div>
      <button
        type="button"
        data-testid="verify-idfraudrisk-modal-button"
        onClick={() => {
          if (!verifyIdFraudRisk.disabled) {
            onClose();
            verifyIdFraudRisk.onVerify();
          }
        }}
        disabled={verifyIdFraudRisk.disabled}
        className="idfraudrisk-modal-verify-button"
      >
        <ShieldCheckIcon size={13} className="idfraudrisk-pill-icon" />
        Mark as reviewed &amp; verified
      </button>
    </div>
  ) : null;

  const verifiedBlock =
    !verifyIdFraudRisk && manualIdFraudRiskVerification ? (
      <div
        className="app-summary-v2 idfraudrisk-modal-verified-box"
        data-testid="idfraudrisk-modal-verified-box"
        style={modalContentWidth}
      >
        <div className="idfraudrisk-modal-verify-text">
          <span
            className="idfraudrisk-pill verified"
            data-testid="idfraudrisk-modal-verified-pill"
          >
            <StrokeCheckIcon size={9} className="idfraudrisk-pill-icon" />
            Manually verified
          </span>
          {(manualIdFraudRiskVerification.reviewerDisplay ||
            manualIdFraudRiskVerification.verifiedAtDisplay) && (
            <span
              className="idfraudrisk-modal-verify-copy"
              data-testid="idfraudrisk-modal-verified-meta"
            >
              {[
                manualIdFraudRiskVerification.reviewerDisplay
                  ? `by ${manualIdFraudRiskVerification.reviewerDisplay}`
                  : null,
                manualIdFraudRiskVerification.verifiedAtDisplay,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          )}
        </div>
        <div
          className="idfraudrisk-retained"
          data-testid="idfraudrisk-modal-verified-retained"
        >
          Original:{" "}
          {manualIdFraudRiskVerification.originalTierLabel ?? "Needs Review"} ·
          retained for audit
        </div>
      </div>
    ) : null;

  return (
    <Modal
      title={
        <strong className="app-summary-v2 vega title">
          {showCosignContent ? "Income guaranteed by Cosign" : modalTitle}
        </strong>
      }
      visible={isOpen}
      close={onClose}
      afterClose={onClose}
      showSubmitButton={false}
      showCancelLink={false}
      titleIcon={
        <StatusIcon status={status} style={{ height: 24, width: 24 }} />
      }
      destroyOnClose
      keyboard
      maskClosable
      style={{ top: 20 }}
    >
      <Modal.Body>
        <div
          className="app-summary-v2 vega flex column items-center"
          style={{ width: "100%" }}
        >
          {showCosignContent ? (
            <div style={modalContentWidth}>
              <div className="vega margin-bottom-medium" style={itemStyle}>
                <div className="vega flex column align-center">
                  <p
                    className="vega text text-center"
                    style={{ marginBottom: "16px" }}
                  >
                    The applicant has income, but it did not meet this
                    property&apos;s requirements. Cosign has guaranteed the
                    income shortfall for this application.
                  </p>

                  <div
                    className="vega flex column align-center"
                    style={{ marginBottom: "16px" }}
                  >
                    <span className="vega text text-center">
                      <strong style={{ color: getStatusColor("pass") }}>
                        Cosign approval status:
                      </strong>
                      <span
                        style={{
                          color: getStatusColor("pass"),
                          marginLeft: "8px",
                        }}
                      >
                        Approved
                      </span>
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={handleViewApprovalLetter}
                    className="vega button primary"
                    style={{
                      marginTop: "8px",
                      padding: "8px 16px",
                      borderRadius: "4px",
                      border: "none",
                      cursor: "pointer",
                      backgroundColor: "#1890ff",
                      color: "#fff",
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#40a9ff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#1890ff";
                    }}
                  >
                    View Cosign approval letter
                  </button>
                </div>
              </div>
            </div>
          ) : hasModalDetails ? (
            <>
              {cardModalDetails.map((section) => (
                <React.Fragment key={section.id || `section-${section.title}`}>
                  <div
                    className="vega margin-bottom-medium"
                    style={modalContentWidth}
                  >
                    {section.title && (
                      <h3 className="vega heading margin-bottom-small">
                        {section.title}
                      </h3>
                    )}

                    {section.items.map((item) => renderModalDetailItem(item))}
                  </div>
                  {/* Reset box sits between the fraud-risk section and Persona Verification. */}
                  {section.id === FRAUD_RISK_SECTION_ID && resetBlock}
                  {section.id === FRAUD_RISK_SECTION_ID && verifyBlock}
                  {section.id === FRAUD_RISK_SECTION_ID && verifiedBlock}
                </React.Fragment>
              ))}
            </>
          ) : (
            <div style={modalContentWidth}>
              {cardDetails.map((detail) => renderCardDetailItem(detail))}
            </div>
          )}

          {vero1Edited && onReviewChanges && (
            <div style={{ ...modalContentWidth, marginTop: 16 }}>
              <Vero1UpdatedNote
                onReviewChanges={onReviewChanges}
                testId="vero1-modal-updated-note"
                surface="modal"
              />
            </div>
          )}

          {/* Fallback: render the reset box at the end when there is no
              dedicated fraud-risk section to anchor it after. */}
          {!hasFraudRiskSection && resetBlock}
          {!hasFraudRiskSection && verifyBlock}
          {!hasFraudRiskSection && verifiedBlock}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ApplicantDetailModal;
